import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Book, Clock, Users, ArrowLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import LanguageSelector from './LanguageSelector';

const CommunityFeed = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchStories(); }, []);

  const fetchStories = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/community/stories`);
      if (res.ok) setStories(await res.json());
    } catch (err) { console.error('Failed to fetch community stories:', err); }
    finally { setLoading(false); }
  };

  const formatDate = (d) => {
    try { return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); }
    catch { return ''; }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50" data-testid="community-page">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <Book className="w-8 h-8 text-purple-500" />
            <h1 className="text-2xl font-bold bg-gradient-to-r from-pink-400 to-purple-400 bg-clip-text text-transparent">Life Echo</h1>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSelector />
            <Button variant="outline" onClick={() => navigate('/')} className="rounded-full" data-testid="back-home-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Home
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-4 py-1.5 rounded-full mb-4 text-sm font-medium">
            <Users className="w-4 h-4" />
            {t('community.title')}
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-3" data-testid="community-title">{t('community.title')}</h2>
          <p className="text-gray-500 text-base">{t('community.subtitle')}</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 border-4 border-purple-200 border-t-purple-500 rounded-full animate-spin" />
          </div>
        ) : stories.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-3xl shadow-sm">
            <Book className="w-16 h-16 text-gray-200 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">{t('community.noStories')}</h3>
            <Button onClick={() => navigate('/')} className="mt-4 btn-primary text-white rounded-full border-0 px-8" data-testid="start-story-btn">
              {t('community.shareYours')}
            </Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="stories-grid">
            {stories.map((story) => (
              <div
                key={story.id}
                className="bg-white rounded-2xl shadow-sm hover:shadow-lg transition-all p-6 cursor-pointer group"
                onClick={() => navigate(`/story/${story.id}`)}
                data-testid={`story-card-${story.id}`}
              >
                <div className="w-12 h-12 bg-gradient-to-br from-pink-100 to-purple-100 rounded-xl flex items-center justify-center mb-4">
                  <Book className="w-6 h-6 text-purple-500" />
                </div>
                <h3 className="text-lg font-bold text-gray-800 mb-1 group-hover:text-purple-600 transition-colors line-clamp-2">
                  {story.title}
                </h3>
                <p className="text-sm text-gray-500 italic mb-3">{story.subtitle}</p>
                <p className="text-sm text-gray-600 line-clamp-3 mb-4">
                  {story.preview}
                </p>
                <div className="flex items-center justify-between text-xs text-gray-400">
                  <div className="flex items-center gap-2">
                    <span>{t('community.by')} {story.user_name}</span>
                    <span>&middot;</span>
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{formatDate(story.created_at)}</span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-purple-500 transition-colors" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CommunityFeed;
